import { Component, ElementRef, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AnswerModel,
  BaseQueryResponse,
  Exam,
  ExamQuery,
  LiveEvent,
  QuestionModel,
} from 'src/app/api/models';
import { ListActionMeta, ListConfig, ListQuery } from '../list/list.types';
import {
  CoursesService,
  ExamsService,
  LiveEventsService,
} from 'src/app/api/services';
import { filter, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { PanelService } from '../panel/panel.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as fileSaver from 'file-saver';
import { DialogService } from '../common/dialog.service';
import { QuickFeedbackOverlayService } from '../common/quick-feedback-overlay/quick-feedback-overlay.service';
import { ImportSnackBarComponent } from '../panel/import-snack-bar.component';
import { MatDialog } from '@angular/material/dialog';
import { UserJourneyService } from '../pages/onboarding-steps/user-journey.service';
import {
  ListInfo,
  NoEntityInfoComponent,
} from '../list/no-entity-info/no-entity-info.component';
import { EditService } from '../edit/edit.service';
import { ExamService } from '../edit/configs/exam.service';
import {
  GridDataComponent,
  multiSelectAction,
} from '../list/grid-data/grid-data.component';
import { ChipType } from 'src/app/shared/components/custom-chip/custom-chip.component';
import { QuestionType } from 'src/app/app.types';
import { SubscriptionLimit } from '../common/dialogs/subscription-limit-warning/subscription-limit-warning.component';
import { SubscriptionLimitService } from '../common/dialogs/subscription-limit-warning/subscription-limit.service';

import {
  getSampleExam,
  INFO_DIALOG_WIDTH,
  validateQuestions,
} from 'src/app/utility/app.utils';
import {
  AmplitudeSource,
  SourceType,
  UIEventEnum,
} from 'src/app/utility/amplitude-tracking/tracking.types';
import { EmbedDialogData } from '../panel/embed-dialog.component';
import { firstValueFrom } from 'rxjs';
import { ExamTemplateDialogComponent } from './exam-template-dialog/exam-template-dialog.component';
import {
  ConfirmConvertFromQuizComponent,
  ShowInfoItem,
} from '../common/dialogs/confirm-convert-from-quiz/confirm-convert-from-quiz.component';
import { Panel, PanelEntity, PanelEntityType } from '../panel/panel.types';
import { ExamPanel } from '../panel/panels/exam-panel';
import { ExamQuestionPanel } from '../panel/panels/exam-question-panel';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { AIImportDialogComponentData } from '../common/dialogs/ai-import-dialog/ai-import-dialog.component';
import { TourTrackType } from '../pages/onboarding/guided-tour';

@Component({
  selector: 'qa-cms-select-exam',
  templateUrl: './cms-exam-select-exam.component.html',
  styleUrls: ['./cms-exam-select-exam.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [GridDataComponent, NoEntityInfoComponent],
})
export class CmsExamSelectExamComponent {
  examListConfig: ListConfig<Exam>;
  listInfo: ListInfo = {
    videoUrl: 'https://www.youtube-nocookie.com/embed/NZz6TEil6lo',
    learnMoreText: 'Exams.exploreMore',
    content: 'Exams.examContent',
    links: [
      {
        label: 'Exams.examLinks.whatUseExamFor',
        url: 'https://quizacademy.de/akademie/examen/#Einsatzm%C3%B6glichkeiten',
      },
      {
        label: 'Exams.examLinks.howCreateExam',
        url: 'https://quizacademy.de/akademie/examen/#Examen+erstellen',
      },
      {
        label: 'Exams.examLinks.howAddExams',
        url: 'https://quizacademy.de/akademie/examen/#Examen+hinzuf%C3%BCgen',
      },
      {
        label: 'Exams.examLinks.howReleaseExam',
        url: 'https://quizacademy.de/akademie/examen/#Examen+freigeben',
      },
      {
        label: 'Exams.examLinks.furtherHelp',
        url: 'https://quizacademy.de/akademie/examen/',
      },
    ],
  };

  constructor(
    private examsService: ExamsService,
    elementRef: ElementRef<HTMLElement>,
    private router: Router,
    private translator: TranslateService,
    private panelService: PanelService,
    private matSnackBar: MatSnackBar,
    private dialog: DialogService,
    private feedbackService: QuickFeedbackOverlayService,
    private matDialog: MatDialog,
    private userJourney: UserJourneyService,
    private editService: EditService,
    private subscriptionLimit: SubscriptionLimitService,
    private liveEventsService: LiveEventsService,
    private trackingService: TrackingService,
    examPanel: ExamPanel,
    examQuestionsPanel: ExamQuestionPanel,
    route: ActivatedRoute
  ) {
    elementRef.nativeElement.classList.add('qa-cms-exam-select-course');
    dayjs.extend(isBetween);
    dayjs.extend(utc);
    if (!panelService?.panels.length) {
      panelService.init([examPanel, examQuestionsPanel], route);
    }
    const examPanelEntity = panelService.panels.find(
      (panel) => panel.type === PanelEntityType.Exam
    );
    const examListConfig = getExamConfig(
      this.examsService,
      this.router,
      this.translator,
      this.panelService,
      this.matSnackBar,
      this.dialog,
      this.userJourney,
      this.editService,
      this.subscriptionLimit,
      this.feedbackService,
      this.matDialog,
      this.liveEventsService,
      examPanelEntity,
      this.trackingService
    );
    examListConfig.columns.forEach((column) => {
      if (column.name === 'duration') {
        column.icon = (meta) => {
          const exam = meta.row;
          if (exam.start_time && exam.end_time && exam.status) {
            return dayjs(new Date()).isBetween(
              exam.start_time.split(' ').join('T') + 'Z',
              dayjs(exam.end_time.split(' ').join('T') + 'Z'),
              null,
              '[]'
            )
              ? 'access_time'
              : '';
          } else {
            return '';
          }
        };
      }
    });
    this.examListConfig = {
      ...examListConfig,
      multiselectActions: [
        {
          icon: 'delete',
          name: 'question.tooltip.delete',
          type: 'button',
          click: (meta) => {
            return new Promise((resolve) => {
              this.dialog
                .confirmDelete(
                  null,
                  this.translator.instant('common.message.deleteMulti', {
                    count: meta.rows.length,
                  })
                )
                .pipe(filter((isConfirmed) => isConfirmed))
                .subscribe(async () => {
                  await multiSelectAction(
                    meta.rows,
                    async (item: any) => {
                      await this.examsService
                        .deleteExam({ id: item.id })
                        .toPromise();
                      return true;
                    },
                    `${this.translator.instant(
                      'common.label.deleting'
                    )} ${this.translator.instant('common.label.exam')}`,
                    this.matDialog
                  );
                  meta.dataSource.refresh();
                  resolve();
                });
            });
          },
        },
      ],
    };
  }

  async create() {
    const exam = await this.editService.open(ExamService);
    this.router.navigateByUrl(`/cms/exams/${exam.id}`);
  }
}

export function getExamConfig(
  exams: ExamsService,
  router: Router,
  translator: TranslateService,
  panelService: PanelService,
  matSnackBar: MatSnackBar,
  dialog: DialogService,
  userJourney: UserJourneyService,
  editService: EditService,
  subscriptionLimit: SubscriptionLimitService,
  feedbackService?: QuickFeedbackOverlayService,
  matDialog?: MatDialog,
  liveEvents?: LiveEventsService,
  examPanel?: Panel<PanelEntity>,
  trackingService?: TrackingService
): ListConfig<Exam> {
  function onTrackExamMarket() {
    if (!feedbackService) return;
    dialog
      .confirm({
        title: translator.instant('exam.market.feedback.title'),
        text: translator.instant('exam.market.feedback.text'),
        buttons: [
          {
            text: translator.instant('common.label.no'),
            color: 'warning',
            icon: 'close',
            value: 'no',
          },
          {
            text: translator.instant('common.label.yes'),
            color: 'primary',
            icon: 'done',
            value: 'yes',
          },
        ],
      })
      .subscribe((value: any) => {
        if (['yes', 'no'].includes(value)) {
          matSnackBar.open(translator.instant('exam.market.feedback.thanks'));
          feedbackService.interestInMarket(
            'exam',
            value === 'yes' ? true : false
          );
        }
      });
  }

  async function importFromJson(
    meta: ListActionMeta<Exam>,
    source?: SourceType,
    item?: Exam
  ) {
    const jsonFile = item || ((await panelService.promptFile('json')) as Exam);
    if (jsonFile?.questions) {
      jsonFile?.questions.forEach((q) => {
        delete q.id;
        if (q.type === QuestionType.FREETEXT && q?.answers?.length) {
          q.answers = [
            {
              text: q.answers[0].text,
              is_right: true,
            } as AnswerModel,
          ];
        }
      });
    }
    if (jsonFile?.name) {
      matSnackBar.open(translator.instant('exam.message.importExam'));
      const examToCreate: Exam = _.cloneDeep(jsonFile);
      delete examToCreate.questions;
      delete examToCreate.status;
      if (
        examToCreate.start_time &&
        examToCreate.end_time &&
        examToCreate.duration
      ) {
        const today = dayjs().startOf('day');
        const endDate = dayjs(examToCreate.end_time);
        const isPastScheduled = endDate.isBefore(today);
        if (isPastScheduled) {
          examToCreate.start_time = new Date().toISOString();
          examToCreate.end_time = dayjs(new Date())
            .add(1, 'M')
            .toDate()
            .toISOString();
          examToCreate.duration = 4;
        }
      }
      const sentryInfo = {
        title: `Error while importing exam from JSON`,
        file: 'cms-exam-select-exam.component.ts -> importFromJson()',
        api: 'copyQuestionToExam',
      };
      const { isValid } = validateQuestions(
        sentryInfo,
        jsonFile.questions,
        translator,
        matSnackBar
      );
      if (!isValid) {
        return;
      }
      const body = examToCreate;
      let questionsChunk = [];
      if (jsonFile.questions) {
        if (jsonFile.questions.length > 10) {
          while (jsonFile.questions.length > 0) {
            questionsChunk.push(jsonFile.questions.splice(0, 10));
          }
        } else {
          questionsChunk = [jsonFile.questions];
        }
      }
      const importProgress = {
        current: 0,
        total: questionsChunk.length + 1,
      };
      const snackBarRef = matSnackBar.openFromComponent(
        ImportSnackBarComponent,
        {
          data: { progress: importProgress },
          duration: null,
        }
      );
      const exam = await firstValueFrom(
        exams.createExam({
          body,
          source,
        })
      );
      userJourney.set('importedExam');
      importProgress.current += 1;
      await importQuestions(questionsChunk, exam, importProgress);
      snackBarRef.dismiss();
      matSnackBar.open(translator.instant('exam.message.importExamSuccess'));
      if (meta && meta?.dataSource) {
        meta?.dataSource.refresh();
      }
      if (matDialog) {
        matDialog.closeAll();
      }
      router.navigate(['cms', 'exams', exam.id]);
    }
  }

  async function importQuestions(
    questions: QuestionModel[][],
    exam: Exam,
    importProgress
  ) {
    for (const items of questions) {
      await exams.copyQuestionToExam({ id: exam.id, body: items }).toPromise();
      importProgress.current += 1;
    }
  }

  async function duplicateExam(meta: ListActionMeta<Exam>) {
    try {
      matSnackBar.open(translator.instant('content.exam.copyExam'));
      const exam = meta.row;
      delete exam.status;
      const copiedExam = await firstValueFrom(
        exams.createExam({
          body: {
            name: exam.name,
            description: exam.description ? exam.description : null,
            scoring_schema: 'DEFAULT',
            submission_message: exam.submission_message
              ? exam.submission_message
              : '',
          } as Exam,
        })
      );
      if (copiedExam) {
        userJourney.set('createdExam');
        matSnackBar.open(translator.instant('content.exam.linkQuestions'));
        const childData = await exams
          .getQuestionsForExam({ id: exam.id })
          .toPromise();
        if (childData && childData?.length) {
          let questionsChunk = [];
          if (childData.length > 10) {
            while (childData.length > 0) {
              questionsChunk.push(childData.splice(0, 10));
            }
          } else {
            questionsChunk = [childData];
          }
          const importProgress = {
            current: 0,
            total: questionsChunk.length + 1,
          };
          const snackBarRef = matSnackBar.openFromComponent(
            ImportSnackBarComponent,
            {
              data: {
                progress: importProgress,
                message: 'content.exam.questionImport',
              },
              duration: null,
            }
          );
          await linkQuestionsToExam(questionsChunk, copiedExam, importProgress);
          snackBarRef.dismiss();
          matSnackBar.open(translator.instant('content.exam.examCopied'));
          meta.dataSource.refresh();
        } else {
          matSnackBar.open(translator.instant('content.exam.examCopied'));
          meta.dataSource.refresh();
        }
      }
    } catch (error) {
      matSnackBar.open(error);
    }
  }

  async function linkQuestionsToExam(
    questions: QuestionModel[][],
    exam: Exam,
    importProgress
  ) {
    for (const items of questions) {
      await exams.linkQuestionToExam({ id: exam.id, body: items }).toPromise();
      importProgress.current += 1;
    }
  }

  async function onExportJson(
    meta: ListActionMeta<Exam>,
    trackingService: TrackingService
  ) {
    const questions = await firstValueFrom(
      exams.getQuestionsForExam({
        id: meta.row.id,
        embed_answers: true,
      })
    );
    const exam: Exam = _.cloneDeep(meta.row);
    delete exam.created_at;
    delete exam.created_by;
    delete exam.id;
    const fullItem = { ...exam, questions };
    const file = `${JSON.stringify(fullItem)}\n`;
    const blob = new Blob([file], { type: 'application/json' });
    const fileName = fullItem.name.split(' ').join('-').toLowerCase();
    fileSaver.saveAs(blob, `qa-exam-${fileName}.json`);
    trackingService.trackEvent({
      event_type: UIEventEnum.JSON_EXPORTED,
      event_properties: {
        source: AmplitudeSource.EXAM,
      },
    });
  }

  function selectExamTemplate(meta: ListActionMeta<Exam>) {
    matDialog
      .open(ExamTemplateDialogComponent, { width: '30vw' })
      .afterClosed()
      .subscribe((response: any) => {
        if (response?.data) {
          importFromJson(meta, SourceType.TEMPLATE, response.data);
        }
      });
  }

  function wizardImports() {
    return {
      createWithAI: {
        text: translator.instant('content.wizardActions.title.createWithAI'),
        icon: 'auto_awesome',
        description: translator.instant(
          'content.wizardActions.description.createExamWithAI'
        ),
        tag: translator.instant('content.wizardActions.tag.createWithAI'),
        click: async () => {
          const exam = await editService.open(ExamService);
          if (exam && exam.id) {
            router.navigateByUrl(`/cms/exams/${exam.id}`).then(() => {
              matDialog?.closeAll();
              const dialogData: AIImportDialogComponentData = {
                name: null,
                type: null,
                onImport: () => {
                  const queryParams = {
                    type: PanelEntityType.ExamQuestion,
                  };
                  panelService.redirectToImportItems(
                    `/cms/csv-imports/question/${exam.id}`,
                    queryParams,
                    true
                  );
                  matDialog?.closeAll();
                },
              };
              panelService.openChatGprDialog(dialogData);
            });
          }
        },
      },
      createButton: {
        text: translator.instant('content.wizardActions.title.createManually'),
        icon: 'edit',
        description: translator.instant(
          'content.wizardActions.description.createManually'
        ),
        click: async () => {
          const exam = await editService.open(ExamService);
          if (exam && exam.id) {
            router.navigateByUrl(`/cms/exams/${exam.id}`);
            matDialog?.closeAll();
          }
        },
      },
      importButtons: [
        {
          text: translator.instant('content.wizardActions.title.useSampleExam'),
          description: translator.instant(
            'content.wizardActions.description.useSampleExam'
          ),
          icon: 'file_copy',
          click: (dataSource) => {
            userJourney.set('addedTemplateExam');
            getSampleExam(translator.currentLang).then((sExam) => {
              importFromJson({ dataSource }, SourceType.MUSTER, sExam);
            });
            matDialog?.closeAll();
          },
        },
        {
          text: translator.instant(
            'content.wizardActions.title.useTrainingTemplates'
          ),
          description: translator.instant(
            'content.wizardActions.description.useTrainingTemplates'
          ),
          icon: 'shopping_cart',
          click: (dataSource) => selectExamTemplate({ dataSource }),
        },
        {
          text: translator.instant(
            'content.wizardActions.title.importFromJSON'
          ),
          description: translator.instant(
            'content.wizardActions.description.importFromJSON'
          ),
          icon: 'save_alt',
          click: (dataSource) => {
            importFromJson({ dataSource }, SourceType.JSON_IMPORT);
          },
        },
        {
          text: translator.instant('content.wizardActions.title.importFromCSV'),
          description: translator.instant(
            'content.wizardActions.description.importFromCSV'
          ),
          icon: 'save_alt',
          click: () => {
            panelService.importCSV(ExamService, PanelEntityType.ExamQuestion);
          },
        },
        {
          text: translator.instant(
            'content.wizardActions.title.convertFromQuiz'
          ),
          description: translator.instant(
            'content.wizardActions.description.examFromQuiz'
          ),
          icon: 'transform',
          click: () => {
            router.navigateByUrl('/cms/exam-from-quiz').then(() => {
              if (matDialog) {
                matDialog.closeAll();
              }
            });
          },
        },
        {
          text: translator.instant(
            'content.wizardActions.title.convertFromLiveEvent'
          ),
          description: translator.instant(
            'content.wizardActions.description.convertFromLiveEvent'
          ),
          icon: 'transform',
          disabled: true,
        },
      ],
    };
  }

  return {
    wizardImports: wizardImports(),
    createButton: {
      options: [
        {
          text: translator.instant('content.exam.addMenu.createNewExam'),
          icon: 'add',
          click: async () => {
            const exam = await editService.open(ExamService);
            if (exam && exam.id) {
              router.navigateByUrl(`/cms/exams/${exam.id}`);
            }
          },
        },
      ],
    },
    imports: [
      {
        title: translator.instant('content.exam.title.importSampleExam'),
        description: translator.instant(
          'content.exam.description.importSampleExam'
        ),
        action: {
          icon: 'code',
          click: (dataSource) => {
            userJourney.set('addedTemplateExam');
            getSampleExam(translator.currentLang).then((sExam) => {
              importFromJson({ dataSource }, SourceType.MUSTER, sExam);
            });
          },
          text: translator.instant('content.exam.button.importSampleExam'),
        },
      },
      {
        title: translator.instant('content.exam.title.useTrainingTemplates'),
        description: translator.instant(
          'content.exam.description.useTrainingTemplates'
        ),
        action: {
          text: translator.instant('content.exam.button.useTrainingTemplates'),
          icon: 'save_alt',
          click: (dataSource) => selectExamTemplate({ dataSource }),
        },
      },
      {
        title: translator.instant('content.exam.title.importFromJSON'),
        description: translator.instant(
          'content.exam.description.importFromJSON'
        ),
        action: {
          icon: 'code',
          click: (dataSource) => {
            importFromJson({ dataSource }, SourceType.JSON_IMPORT);
          },
          text: translator.instant('content.exam.addMenu.examFromJSON'),
        },
      },
      {
        title: translator.instant('content.exam.title.examFromQuiz'),
        description: translator.instant(
          'content.exam.description.examFromQuiz'
        ),
        action: {
          icon: 'code',
          click: () => {
            router.navigateByUrl('/cms/exam-from-quiz').then(() => {
              if (matDialog) {
                matDialog.closeAll();
              }
            });
          },
          text: translator.instant('content.exam.addMenu.examFromQuiz'),
        },
      },
    ],
    hideWrapper: false,
    actions: [
      {
        icon: 'play_circle',
        name: 'select',
        tooltip: 'exam.selectExam',
        type: 'button',
        isDisabled: (meta) => {
          return meta?.row ? meta.row.status === 'END' : false;
        },
        click: (meta) => {
          router.navigate(['/cms/exams', meta.row.id], {
            queryParams: {
              selectedTab: 'publish',
            },
          });
        },
      },
      {
        icon: 'timeline',
        name: 'stats',
        label: translator.instant('content.quiz.actionMenu.openStatistics'),
        isDisabled: (meta) => {
          if (!meta?.row?.status) {
            return true;
          } else {
            return meta?.row?.status
              ? meta.row.status === 'OPEN_FOR_REGISTRATION'
              : false;
          }
        },
        click: (meta) => {
          router.navigate(['/cms/exams', meta.row.id], {
            queryParams: {
              selectedTab: 'results',
            },
          });
        },
        tooltip: 'content.quiz.actionMenu.openStatistics',
        type: 'button',
      },
    ],
    chip: {
      headerText: 'common.label.status',
      name: 'status',
      hide: (meta) => !meta?.row?.status,
      label: (meta) => {
        return meta.row?.status === 'OPEN_FOR_REGISTRATION'
          ? 'exam.setup.label.regisStarted'
          : meta.row?.status === 'STARTED'
          ? 'exam.setup.label.inProgress'
          : 'exam.setup.label.completed';
      },
      type: (meta) =>
        meta.row?.status === 'END' ? ChipType.PRIMARY_LIGHT : ChipType.DEFAULT,
      icon: (meta) => {
        return meta.row?.status === 'END' ? 'check' : 'timer';
      },
    },
    actionsMenu: [
      {
        icon: 'print',
        name: 'print',
        label: translator.instant('content.exam.actionMenu.saveAsPDF'),
        type: 'button',
        click: ({ row }) => {
          window.open(
            `${window.location.origin}/cms/exams/print/${row.id}`,
            '_blank'
          );
        },
      },
      {
        click: () => onTrackExamMarket(),
        name: 'share',
        icon: 'share',
        label: 'exam.market.feedback.share',
        type: 'button',
      },
      {
        icon: 'copy',
        type: 'button',
        name: 'copy',
        label: translator.instant('content.exam.actionMenu.duplicateExam'),
        click: (meta) => duplicateExam(meta),
      },
      {
        icon: 'play_arrow',
        name: 'convert',
        label: translator.instant('content.quiz.actionMenu.convertToLiveEvent'),
        type: 'button',
        click: (meta) =>
          convertExamToEventAction(
            meta.row,
            exams,
            liveEvents,
            matSnackBar,
            translator,
            router,
            matDialog,
            null,
            null
          ),
      },
      {
        icon: 'save_alt',
        type: 'button',
        name: 'export_json',
        label: translator.instant('content.exam.actionMenu.downloadJSON'),
        click: (meta) => onExportJson(meta, trackingService),
      },
      {
        icon: 'description',
        type: 'button',
        name: 'export_csv',
        label: translator.instant('content.exam.actionMenu.downloadCSV'),
        click: (meta) => {
          panelService.exportItemAsCSV(examPanel, meta.row.id);
        },
      },
      {
        icon: 'code',
        name: 'Embed',
        click: (meta) => {
          const data: EmbedDialogData = {
            pinCode: meta.row.pin_code,
            entity: PanelEntityType.Exam,
          };
          dialog.openEmbedModel(data);
        },
        label: 'content.course.actionMenu.embed',
        type: 'button',
      },
      {
        click: ({ row, dataSource }) => {
          dialog
            .confirmDelete()
            .pipe(
              filter((isConfirmed) => isConfirmed),
              switchMap(() => exams.deleteExam({ id: row.id }))
            )
            .subscribe({
              next: () => {
                matSnackBar.open(
                  translator.instant('content.exam.deleteItem.success')
                );
                dataSource.refresh();
              },
              error: () => {
                matSnackBar.open(
                  translator.instant('content.exam.deleteItem.error')
                );
              },
            });
        },
        icon: 'delete',
        name: 'delete',
        label: translator.instant('quiz.tooltip.delete'),
        type: 'button',
      },
    ],
    activeActionNames: ['select', 'edit', 'chip', 'stats'],
    activeColumnNames: ['id', 'name', 'duration'],
    columns: [
      {
        label: 'ID',
        name: 'id',
        type: 'number',
        sortable: true,
        width: '64px',
      },
      {
        label: 'quiz.table.name',
        name: 'name',
        sortable: true,
        primary: true,
        filter: (value) => {
          return {
            name: value,
          };
        },
        click: (meta) => router.navigate(['/cms/exams', meta.row.id]),
      },
      {
        label: 'exam.table.timeBased',
        name: 'duration',
        accessor: () => '',
        sortable: false,
        width: '110px',
      },
    ],
    showSubscriptionIndicator: true,
    subscriptionLimit: SubscriptionLimit.EXAM_COUNT,
    fetch: (queryParams?: ListQuery<Exam>) => {
      return exams
        .queryExams({ body: queryParams as unknown as ExamQuery })
        .pipe(
          tap((response: BaseQueryResponse) => {
            subscriptionLimit.subscriptionLimit.EXAM_COUNT =
              response.pagination.total;
          })
        );
    },
    query: {
      initial: {
        sorting: [
          {
            option: 'id',
            direction: 'DESC',
          },
        ],
      },
      searchColumnName: 'name',
    },
    name: 'examList',
    title: translator.instant('common.label.exams'),
    tutorial: 'exam',
    showGuidedTour: true,
    extraTutorial: 'https://quizacademy.de/akademie/examen/',
    tutorialLabel: 'dashboardNew.examCard.videoButton',
    extraTutorialLabel: 'dashboardNew.examCard.academicButton',
    progressStepType: TourTrackType.EXAM,
  };
}

export function convertExamToEventAction(
  exam: Exam,
  exams: ExamsService,
  liveEvents: LiveEventsService,
  matSnackBar: MatSnackBar,
  translator: TranslateService,
  router: Router,
  matDialog: MatDialog,
  courses: CoursesService,
  courseId: number
) {
  const dialogRef = matDialog.open<
    ConfirmConvertFromQuizComponent,
    ShowInfoItem
  >(ConfirmConvertFromQuizComponent, {
    data: {
      modelTitle: translator.instant(
        'course.confirmAction.convertExamToEventHint'
      ),
      confirmButtonText: translator.instant(
        'course.confirmAction.convertExamToEvent'
      ),
    },
    width: INFO_DIALOG_WIDTH,
    height: 'auto',
    autoFocus: false,
    panelClass: 'confirm-convert-quiz-modal',
  });
  dialogRef.afterClosed().subscribe((resp) => {
    if (resp) {
      convertExamToEvent(
        exam,
        exams,
        liveEvents,
        matSnackBar,
        translator,
        router,
        courses,
        courseId
      );
    }
  });
}

export async function convertExamToEvent(
  exams: Exam,
  examService: ExamsService,
  eventsService: LiveEventsService,
  matSnackBar: MatSnackBar,
  translator: TranslateService,
  router: Router,
  courses: CoursesService,
  courseId: number
) {
  try {
    let questions = await examService
      .getQuestionsForExam({
        id: exams.id,
      })
      .toPromise();
    questions = questions.filter(
      (q) => q.is_poll_question || q.type !== QuestionType.FREETEXT
    );
    const event = await firstValueFrom(
      eventsService.createLiveEvent({
        body: {
          name: exams.name,
        } as LiveEvent,
        source: SourceType.QUIZ,
      })
    );
    if (courseId) {
      const query = {
        id: courseId,
        body: event,
      };
      await firstValueFrom(courses.addEventToCourse(query));
    }

    await firstValueFrom(
      eventsService.linkQuestionToEvent({
        id: event.id,
        body: questions.map((q) => ({ id: q.id } as QuestionModel)),
      })
    );

    matSnackBar.open(translator.instant('content.exam.convertedEvent'));

    const queryParams = courseId ? { courseId } : null;
    router.navigate(['/cms/live-events', event.id], { queryParams });
  } catch (err) {
    console.log(err);
    matSnackBar.open(
      translator.instant('content.exam.errorWhileConvertingEvent')
    );
  }
}
